import { Alert, Box, Button, Drawer, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import DashboardCard from '/@/components/bayer/dashboard-card';
import ModalV2 from '/@/components/bayer/modal-v2';
import CropMonitoringCards from '/@/components/crop-monitoring/cards-container';
import HarvestLabelAndModal from '/@/components/crop-monitoring/harvest-alert';
import ProgressBar from '/@/components/crop-monitoring/progress-bar';
import ReferenceBar from '/@/components/crop-monitoring/reference-bar';
import TechnologyButtons from '/@/components/crop-monitoring/technology-buttons';
import { usePlantingReportResult } from '/@/components/field-list-v2/components/sidebar/hooks';
import Icon from '/@/components/icon';
import PlantingReportInfoCard from '/@/components/planting-report-info-card';
import Spinner from '/@/components/spinner';
import Switch from '/@/components/switch';
import useUnitProvider from '/@/core/context/unit';
import { useAllCatalogProducts } from '/@/hooks/catalog';
import { useFieldById } from '/@/hooks/fields';
import { useHasWriteAccess, useIsValidatedConsultant, useIsValidatedRV } from '/@/hooks/session';
import { useVisitReports } from '/@/hooks/visit-reports';
import { _ } from '/@/locale';
import { getCatalogBrand } from '/@/store/catalog/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { HistoricalWeather } from '/@/types/weather/forecast';
import { formatDate } from '/@/utils';
import useIsMobile from '/@/utils/hooks/is-mobile';
import useIsPlaywriteEnvironment from '/@/utils/hooks/is-playwrite-environment';
import Title from '/@/v3/components/title';
import CropProtectionWhenToApply from '/@/views/product-exploration/crop-protection/when-to-apply';
import CreateReportModal from '../create-report-modal';
import CropMonitoringDialog from '../creation-dialog';
import VisitReportList from '../visit-reports-list';
import YieldEstimationForm from '../yield-estimation-form';

const FieldMonitoringDetail = ({
    fieldId,
    cropMonitoring,
    showCropProtection,
    setShowCropProtection,
    setTechnology,
    technology,
    showAlert,
    historicalWeather,
    loadingWeather,
    references,
}: {
    fieldId: number;
    cropMonitoring?: CropMonitoring;
    showCropProtection: boolean;
    setShowCropProtection: (show: boolean) => void;
    setTechnology: (technology: string) => void;
    technology: string;
    showAlert: boolean;
    historicalWeather: HistoricalWeather;
    loadingWeather: boolean;
    references: { text: string; color: string }[];
}) => {
    const units = useUnitProvider();
    const fieldQuery = useFieldById({ id: fieldId });
    const isMobile = useIsMobile();
    const country = useSelector(getSessionCountry);
    const cropProtectionBrand = useSelector((state) => getCatalogBrand(state, 'crop_protection'));
    const cropProtectionProductsQuery = useAllCatalogProducts({
        brand: cropProtectionBrand?.name,
        crop: 'crop_protection',
        country: country!.code!,
        cropProtectionCrop: undefined,
        cropProtectionProductType: undefined,
    });
    const cropProtectionProductsEnabled = cropProtectionBrand && cropProtectionProductsQuery.data?.results?.length > 0;

    const [yieldEstimationDrawerIsOpen, setYieldEstimationDrawerIsOpen] = useState(false);
    const [closeModalIsOpen, setCloseModalIsOpen] = useState(false);
    const [CMFormModalIsOpen, setCMFormModalIsOpen] = useState(false);
    const [createReportModalIsOpen, setCreateReportModalIsOpen] = useState(false);
    const today = new Date();
    const plantingDate = cropMonitoring ? new Date(cropMonitoring.plantingDate) : null;
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const phenology = cropMonitoring?.cornCropMonitoring?.phenology;
    const isRv = useIsValidatedRV();
    const isConsultant = useIsValidatedConsultant();
    const theme = useTheme();
    const visitReportsQuery = useVisitReports(
        {
            fieldId: fieldId,
            campaign: fieldQuery.data?.campaign,
            status: !(isRv || isConsultant) ? 'saved' : undefined,
        },
        { enabled: !!fieldQuery.data }
    );

    const isPlaywriteEnvironment = useIsPlaywriteEnvironment();

    const title = useMemo(() => {
        const options = {
            corn: _('Híbrido sembrado'),
        };
        return options[cropMonitoring?.crop as keyof typeof options] || _('Variedad sembrada'); // for wheat and soy
    }, [cropMonitoring?.crop]);

    const graphic = {
        corn: (
            <ProgressBar
                technology={technology}
                cropMonitoring={cropMonitoring || { stages: [] }}
                showCropProtection={showCropProtection}
                showExpectedFloweringDate={showAlert}
                showAlert={showAlert}
            />
        ),
        soy: <CropProtectionWhenToApply initialCrop='soy' showCropProtectionProducts={showCropProtection} />,
        wheat: <CropProtectionWhenToApply initialCrop='wheat' showCropProtectionProducts={showCropProtection} />,
        barley: <CropProtectionWhenToApply initialCrop='barley' showCropProtectionProducts={showCropProtection} />,
    };
    const canEditResource = useHasWriteAccess();
    const recentPlantingResult = usePlantingReportResult(fieldQuery.data);

    return (
        <>
            <Grid2 container direction='row' spacing={3} mx={'-12px !important'}>
                <Grid2 xs={12} md={4} sx={{ height: 'auto' }}>
                    {cropMonitoring?.active ? (
                        <DashboardCard
                            data-testid='field-monitoring-detail-active-crop-card'
                            title={title}
                            number={cropMonitoring.productName}
                            subtitle={
                                cropMonitoring?.result?.plantedArea
                                    ? `${_('Superficie sembrada')}: ${units.toArea(cropMonitoring?.result?.plantedArea, 2).toString()}`
                                    : undefined
                            }
                        />
                    ) : (
                        <DashboardCard
                            title={_('Próximo cultivo')}
                            data-testid='field-monitoring-detail-next-crop-card'
                            subtitle={_('Agregá el próximo cultivo para poder hacer un mejor seguimiento')}
                            buttonProps={
                                recentPlantingResult
                                    ? undefined
                                    : {
                                          onClick: () => setCMFormModalIsOpen(true),
                                          startIcon: (
                                              <Icon
                                                  type='agronomy'
                                                  size='xxs'
                                                  sx={{
                                                      fill: canEditResource
                                                          ? theme.palette.common.white
                                                          : theme.palette.action.disabled,
                                                  }}
                                                  padding={false}
                                              />
                                          ),
                                          children: _('Agregar cultivo'),
                                          size: 'small',
                                          disabled: !canEditResource,
                                      }
                            }
                        >
                            {recentPlantingResult && (
                                <PlantingReportInfoCard
                                    recentPlantingResult={recentPlantingResult}
                                    actions={
                                        <Button
                                            variant='contained'
                                            color='primary'
                                            startIcon={
                                                <Icon
                                                    type='agronomy'
                                                    size='xxs'
                                                    sx={{ fill: theme.palette.common.white }}
                                                    padding={false}
                                                />
                                            }
                                            onClick={() => setCMFormModalIsOpen(true)}
                                        >
                                            {_('Agregar próximo cultivo')}
                                        </Button>
                                    }
                                />
                            )}
                        </DashboardCard>
                    )}
                </Grid2>
                <Grid2 xxs={12} md={4} sx={{ height: 'auto' }}>
                    {cropMonitoring?.harvestDate ? (
                        <DashboardCard
                            title={_('Fecha de cosecha')}
                            number={
                                cropMonitoring?.harvestDate
                                    ? formatDate(new Date(cropMonitoring.harvestDate))
                                    : undefined
                            }
                            buttonProps={{
                                onClick: () => {
                                    setCMFormModalIsOpen(true);
                                },
                                startIcon: <Icon type='agronomy' size='xxs' sx={{ fill: 'white' }} padding={false} />,
                                children: _('Agregar cultivo'),
                                size: 'small',
                                disabled: !canEditResource,
                            }}
                        />
                    ) : (
                        <DashboardCard
                            title={_('Fecha de siembra')}
                            number={cropMonitoring ? formatDate(new Date(cropMonitoring.plantingDate)) : undefined}
                            subtitle={
                                cropMonitoring
                                    ? undefined
                                    : _('Acá vas a ver la fecha de siembra una vez que comience la campaña')
                            }
                        />
                    )}
                </Grid2>
                <Grid2 xs={12} md={4} sx={{ height: 'auto' }}>
                    <DashboardCard
                        title={_('Ambiente')}
                        data-testid='field-monitoring-detail-environment-card'
                        number={
                            cropMonitoring
                                ? units.toYield(cropMonitoring.environment, cropMonitoring.crop, 2).toString()
                                : undefined
                        }
                        subtitle={
                            cropMonitoring ? undefined : _('Acá vas a ver el ambiente una vez que comience la campaña')
                        }
                    />
                </Grid2>
            </Grid2>

            {fieldQuery.data && !isPlaywriteEnvironment && (
                <HarvestLabelAndModal
                    field={fieldQuery.data}
                    cropMonitoring={cropMonitoring}
                    //
                    createReportModalIsOpen={createReportModalIsOpen}
                    setCreateReportModalIsOpen={setCreateReportModalIsOpen}
                    //
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    //
                    showErrorModal={showErrorModal}
                    showAlert
                    automaticOpenModals
                />
            )}

            {!!visitReportsQuery.data?.count && <VisitReportList visitReports={visitReportsQuery.data?.results} />}

            {cropMonitoring && (
                <Stack alignItems='center' justifyContent='space-between' mt={4} spacing={2} direction='row'>
                    <Box>
                        <ReferenceBar references={references} crop={cropMonitoring?.crop} />
                    </Box>
                    <Stack direction='row' spacing={1} alignItems='center' justifyContent='right'>
                        {!isMobile && showCropProtection && (
                            <TechnologyButtons
                                technology={technology}
                                isMobile={false}
                                setTechnology={(e) => setTechnology(e)}
                                crop={cropMonitoring?.crop}
                            />
                        )}
                        {cropProtectionProductsEnabled && (
                            <Stack spacing={1} alignItems='center' direction='row'>
                                <Typography
                                    variant='body2'
                                    sx={{
                                        fontWeight: 'bold',
                                        color: (t) => t.palette.primary.main,
                                    }}
                                >
                                    {_('Protección de cultivos')}
                                </Typography>
                                <Switch checked={showCropProtection} onChange={(_, v) => setShowCropProtection(v)} />
                            </Stack>
                        )}
                        {isMobile && showCropProtection && (
                            <TechnologyButtons
                                technology={technology}
                                isMobile={true}
                                setTechnology={(e) => setTechnology(e)}
                                crop={cropMonitoring?.crop}
                            />
                        )}
                    </Stack>
                </Stack>
            )}

            {cropMonitoring && (
                <Stack>
                    {graphic[cropMonitoring?.crop as keyof typeof graphic]}
                    {plantingDate &&
                        !phenology &&
                        cropMonitoring?.crop === 'corn' &&
                        today.getTime() > plantingDate?.getTime() && (
                            <Alert severity='info' sx={{ my: 4 }}>
                                {_('Estamos preparando los datos para que sigas el avance del cultivo en cada etapa.')}
                            </Alert>
                        )}
                </Stack>
            )}

            <Stack sx={{ mt: { xs: 3, md: 1 }, mb: 2, height: '100%' }}>
                {!loadingWeather ? (
                    <CropMonitoringCards
                        cropMonitoring={cropMonitoring}
                        historicalWeather={historicalWeather}
                        technology={technology}
                        fieldId={fieldId}
                        setYieldEstimationDrawerIsOpen={setYieldEstimationDrawerIsOpen}
                        setCMFormModalIsOpen={setCMFormModalIsOpen}
                    />
                ) : (
                    <Grid container justifyContent='center' alignItems='center' item xs={12} sx={{ height: '100%' }}>
                        <Spinner />
                    </Grid>
                )}
                <Stack my={2} pb={2}>
                    <Title size='sm'>
                        {_(
                            '(*) Toda la información meteorológica: Temperatura, Humedad, Precipitaciones, Viento, es otorgada por The Weather Company.'
                        )}
                    </Title>
                </Stack>
            </Stack>
            {cropMonitoring && (
                <Drawer
                    open={yieldEstimationDrawerIsOpen}
                    onClose={() => {
                        setCloseModalIsOpen(true);
                    }}
                    anchor='right'
                >
                    <YieldEstimationForm
                        cropMonitoring={cropMonitoring}
                        setYieldEstimationDrawerIsOpen={setYieldEstimationDrawerIsOpen}
                        setCloseModalIsOpen={setCloseModalIsOpen}
                    />
                </Drawer>
            )}
            <ModalV2
                open={closeModalIsOpen}
                onClose={() => {
                    setCloseModalIsOpen(false);
                }}
                actions={
                    <>
                        <Button
                            variant='text'
                            color='secondary'
                            onClick={() => {
                                setCloseModalIsOpen(false);
                            }}
                        >
                            {_('Seguir editando')}
                        </Button>
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={() => {
                                setCloseModalIsOpen(false);
                                setYieldEstimationDrawerIsOpen(false);
                            }}
                        >
                            {_('Confirmar')}
                        </Button>
                    </>
                }
                title={_('¿Querés salir sin guardar?')}
                actionsProps={{ sx: { justifyContent: 'end' } }}
                maxWidth={'sm'}
            >
                <Typography color={'primary'} variant='body1'>
                    {_('Al cancelar, los datos ingresados no se guardarán y tendrás que volver a cargarlos.')}
                </Typography>
            </ModalV2>
            {cropMonitoring && fieldQuery.data && (
                <CreateReportModal
                    field={fieldQuery.data}
                    cropMonitoring={cropMonitoring}
                    isOpen={createReportModalIsOpen}
                    setIsOpen={setCreateReportModalIsOpen}
                    type={selectedOption}
                    setShowErrorModal={setShowErrorModal}
                    showErrorModal={showErrorModal}
                />
            )}
            {fieldQuery.data && (
                <CropMonitoringDialog
                    open={CMFormModalIsOpen}
                    setClose={() => setCMFormModalIsOpen(false)}
                    field={fieldQuery.data}
                    entry_source='field-detail'
                    plantingReport={recentPlantingResult ?? undefined}
                />
            )}
        </>
    );
};

export default FieldMonitoringDetail;
